import { useMemo } from 'react';
import { useEmployeeProfilePhoenix } from '@pflegenavi/frontend/api-nursing-home';
import type { EmployeeProfilePhoenix } from '@pflegenavi/frontend/api-nursing-home';

export type EmployeeRole = 'admin' | 'nursing-home-manager' | 'employee';

export const ADMIN_OR_MANAGER_ROLES: EmployeeRole[] = [
  'admin',
  'nursing-home-manager',
];

/**
 * Whether the employee holds a tenant-level admin association, which grants
 * access to everything in the tenant.
 *
 * @example
 * isGlobalTenantAdmin(employeeProfile) // => true
 */
export const isGlobalTenantAdmin = (
  employeeProfile: EmployeeProfilePhoenix | undefined
): boolean =>
  Boolean(
    employeeProfile?.resource_associations?.['tenant']?.some(
      (association) => association.role.name === 'admin'
    )
  );

// Legacy tenants without any nursing home association do not express the role
// per home, so `nursing-home-manager` is granted blanket.
const hasLegacyBlanketRole = (
  employeeProfile: EmployeeProfilePhoenix,
  roles: EmployeeRole[]
): boolean =>
  !employeeProfile.use_new_authorization &&
  !employeeProfile.resource_associations?.['nursing_home']?.length &&
  roles.includes('nursing-home-manager');

/**
 * Ids of the nursing homes the employee holds one of `roles` in. Empty for a
 * global tenant admin, who holds them everywhere - check
 * {@link isGlobalTenantAdmin} first.
 *
 * @example
 * nursingHomeIdsWithRole(employeeProfile, ['admin']) // => ['nh-1']
 */
export const nursingHomeIdsWithRole = (
  employeeProfile: EmployeeProfilePhoenix | undefined,
  roles: EmployeeRole[] = ADMIN_OR_MANAGER_ROLES
): string[] =>
  employeeProfile?.resource_associations?.['nursing_home']
    ?.filter((association) => roles.includes(association.role.name))
    .map((association) => association.resource_id) ?? [];

/**
 * Whether the employee holds one of `roles` in *any* nursing home. Unlike
 * {@link checkIfUserHasAccess} this does not depend on a selected nursing home,
 * which is what the scope selector needs (ENG-2503).
 *
 * @example
 * hasRoleInAnyNursingHome(employeeProfile) // => true
 */
export const hasRoleInAnyNursingHome = (
  employeeProfile: EmployeeProfilePhoenix | undefined,
  roles: EmployeeRole[] = ADMIN_OR_MANAGER_ROLES
): boolean => {
  if (!employeeProfile) {
    return false;
  }

  return (
    isGlobalTenantAdmin(employeeProfile) ||
    hasLegacyBlanketRole(employeeProfile, roles) ||
    nursingHomeIdsWithRole(employeeProfile, roles).length > 0
  );
};

/**
 * Whether the employee holds one of `roles` for the given nursing home.
 *
 * @example
 * checkIfUserHasAccess(employeeProfile, 'nh-1', ['admin']) // => true
 */
export const checkIfUserHasAccess = (
  employeeProfile: EmployeeProfilePhoenix | undefined,
  selectedNursingHomeId: string | undefined,
  roles: EmployeeRole[] = ADMIN_OR_MANAGER_ROLES
): boolean => {
  if (!selectedNursingHomeId || !employeeProfile) {
    return false;
  }

  return (
    isGlobalTenantAdmin(employeeProfile) ||
    hasLegacyBlanketRole(employeeProfile, roles) ||
    nursingHomeIdsWithRole(employeeProfile, roles).includes(
      selectedNursingHomeId
    )
  );
};

/**
 * As {@link checkIfUserHasAccess}, fetching the employee profile itself. Web
 * and mobile pass the selected nursing home from their own context.
 *
 * @example
 * useEmployeeHasNursingHomeRole(selectedNursingHome?.id, ['admin'])
 */
export const useEmployeeHasNursingHomeRole = (
  selectedNursingHomeId: string | undefined,
  roles: EmployeeRole[],
  enabled = true
): boolean => {
  const { data: employeeProfile } = useEmployeeProfilePhoenix({ enabled });

  return useMemo(
    () => checkIfUserHasAccess(employeeProfile, selectedNursingHomeId, roles),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employeeProfile, selectedNursingHomeId, ...roles]
  );
};

export interface NursingHomeAdminScope {
  isGlobalTenantAdmin: boolean;
  /** Empty when `isGlobalTenantAdmin`: they hold the role in every home. */
  nursingHomeIds: string[];
}

/**
 * The nursing homes the employee is admin or manager in, aggregated across the
 * whole profile. `undefined` until the profile arrives, so callers can wait
 * instead of acting on an empty picture.
 *
 * @example
 * useNursingHomeAdminScope() // => { isGlobalTenantAdmin: false, nursingHomeIds: ['nh-1'] }
 */
export const useNursingHomeAdminScope = ():
  | NursingHomeAdminScope
  | undefined => {
  const { data: employeeProfile } = useEmployeeProfilePhoenix({});

  return useMemo(() => {
    if (!employeeProfile) {
      return undefined;
    }

    return {
      isGlobalTenantAdmin: isGlobalTenantAdmin(employeeProfile),
      nursingHomeIds: nursingHomeIdsWithRole(employeeProfile),
    };
  }, [employeeProfile]);
};
